import styled from "@emotion/styled";
import SiteRegistrationProps from "@mediaspace/features/registration/registration/SiteRegistrationProps";
import { Typography } from "@kaltura/ds-react-components";
import { systemWidth } from "@kaltura/mediaspace-shared-styled";
import { HtmlReactParser } from "@kaltura/mediaspace-shared-utils";
import Dates from "../common/Dates";
import { RegistrationButton } from "./RegistrationButton";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";

export interface RegistrationProps {
    /**
     * props for registration form
     */
    siteRegistrationProps: SiteRegistrationProps;

    /**
     * simple text, mandatory - admin Text: Mandatory field. Enter the title of the component.
     */
    title: string;

    /**
     * simple text, mandatory, default: Register – admin Text: Enter the registration button's text (default is "register").
     */
    buttonText: string;

    /**
     * unix, optional - Enter a start time to show on the component. Leave empty to hide the schedule line.
     */
    scheduleTimeFrom?: number;

    /**
     * unix, optional - Enter an end time to show on the component. Leave empty to hide the end time.
     */
    scheduleTimeTo?: number;

    /**
     * text with basic tags (bold, italic, underline, link) , optional -
     * text to show on the component while in the schedule time
     */
    inScheduleText?: string;

    /**
     * (bold, italic, underline, link) text, optional -
     * text to show on the component while out of the schedule time, or if no schedule time is selected.
     */
    outofScheduleText?: string;

    /**
     * open registration form drawer on start
     */
    startOpen?: boolean;
}

const StyledRegistration = styled("div")(({ theme }) => ({
    "&": systemWidth({ theme }),
    textAlign: "center",
    margin: theme.spacing(6),
}));

const StyledRegistrationButton = styled(RegistrationButton)(({ theme }) => ({
    marginTop: theme.spacing(3),
}));

const StyledDates = styled(Dates)(({ theme }) => ({
    marginTop: theme.spacing(2),
}));

/**
 * component that allows opening the registration form on the side of the screen
 */
function Registration({
    title,
    buttonText,
    scheduleTimeTo,
    scheduleTimeFrom,
    inScheduleText,
    outofScheduleText,
    siteRegistrationProps,
    startOpen,
}: RegistrationProps) {
    const sendButtonAnalytics = useButtonAnalytics();

    // "during event" means we know when the event started, and it's in the past,
    // and if we know when it ends, it should be in the future.
    const now = Math.floor(Date.now() / 1000);
    let duringEvent = !!scheduleTimeFrom && now > scheduleTimeFrom;
    if (scheduleTimeTo) {
        duringEvent = duringEvent && now < scheduleTimeTo;
    }
    const showInScheduleText = !!inScheduleText && duringEvent;
    const showOutofScheduleText = !!outofScheduleText && !duringEvent;

    return (
        <StyledRegistration>
            <Typography variant={"h2"}>{title}</Typography>
            {showInScheduleText && <Typography variant={"body1"}>{HtmlReactParser(inScheduleText)}</Typography>}
            {showOutofScheduleText && <Typography variant={"body1"}>{HtmlReactParser(outofScheduleText)}</Typography>}
            {scheduleTimeFrom && <StyledDates startTime={scheduleTimeFrom} endTime={scheduleTimeTo} />}
            <StyledRegistrationButton
                siteRegistrationProps={siteRegistrationProps}
                isRegistrationFormOpen={startOpen}
                onRegistrationFormOpen={() =>
                    sendButtonAnalytics("Registration Modal Box - Launch", ButtonClickAnalyticsType.EXPAND)
                }
                onRegistrationFormClose={() =>
                    sendButtonAnalytics("Registration Modal Box - Close", ButtonClickAnalyticsType.CLOSE)
                }
                onConfirmationConfirmExit={() =>
                    sendButtonAnalytics("Registration Modal Box - Approve Close", ButtonClickAnalyticsType.CLOSE)
                }
                onConfirmationCancel={() =>
                    sendButtonAnalytics("Registration Modal Box - Cancel Close", ButtonClickAnalyticsType.CLOSE)
                }
            >
                {buttonText}
            </StyledRegistrationButton>
        </StyledRegistration>
    );
}

export default Registration;
